import { useEffect, useState } from 'react'
import { useAudioEngine } from '../hooks/audio-engine.js'
import type { AudioInstrument } from '../instruments/index.js'

/**
 * Instrument hook — wires the instrument up and hands back the id the engine registered it under.
 *
 * The id matters: `addInstrument` keys the engine's registry by a generated uuid, not by `name`,
 * and `engine.play(notes, id)` wants that uuid. Without it a caller can only call `play(notes)`,
 * which resolves to `Object.keys(instruments)[0]` — the first instrument anyone registered. One
 * instrument on the page hides that; two do not, and the wrong one sounds with nothing logged.
 *
 * `null` until the registration effect has run, and whenever there is no engine or no instrument.
 */
export const useInstrument = (name: string, instrument?: AudioInstrument): string | null => {
  const audioEngine = useAudioEngine(true)
  const [instrumentId, setInstrumentId] = useState<string | null>(null)

  useEffect(() => {
    if (!(name && audioEngine && instrument)) {
      setInstrumentId(null)
      return
    }
    const id = audioEngine.addInstrument(name, instrument)
    setInstrumentId(id)
    return () => {
      setInstrumentId(null)
      audioEngine.removeInstrument(id)
    }
  }, [audioEngine, instrument, name])

  return instrumentId
}
